import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import styles from './ids-data-label.scss';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsLocaleMixin from '../../mixins/ids-locale-mixin/ids-locale-mixin';
import IdsLabelPositionMixin from '../../mixins/ids-label-position-mixin/ids-label-position-mixin';
import IdsElement from '../../core/ids-element';
import type IdsText from '../ids-text/ids-text';
import { stringToBool } from '../../utils/ids-string-utils/ids-string-utils';
import { MaskType } from '../ids-text/ids-text';
import { isValidInteger } from '../../utils/ids-number-utils/ids-number-utils';

const Base = IdsLabelPositionMixin(
  IdsLocaleMixin(
    IdsEventsMixin(
      IdsElement
    )
  )
);

/**
 * IDS Data Label Component
 * @type {IdsDataLabel}
 * @inherits IdsElement
 * @mixes IdsLocaleMixin
 * @mixes IdsEventsMixin
 * @mixes IdsLabelPositionMixin
 */
@customElement('ids-data-label')
@scss(styles)
export default class IdsDataLabel extends Base {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
  }

  /**
   * Return the attributes we handle as getters/setters
   * @returns {Array} The propertires in an array
   */
  static get attributes() {
    return [
      ...super.attributes,
      attributes.SHOW_COLON,
      attributes.LABEL,
      attributes.LANGUAGE,
      attributes.FONT_WEIGHT,
      attributes.MASK_TYPE,
      attributes.TEXT_MASK_CHARACTER,
      attributes.MASKED
    ];
  }

  /**
   * Create the Template for the contents
   * @returns {string} The template
   */
  template(): string {
    return `<div class="ids-data-label">
        ${this.label ? this.labelText : ''}
        <ids-text class="description" font-weight="${this.fontWeight}" font-size="16"><slot></slot></ids-text>
      </div>`;
  }

  /**
   * Sets the mask type for the text component
   * @param {MaskType} value The mask type to apply ('masked-only', 'button-mask', 'icon-mask', or null)
   */
  set maskType(value: MaskType) {
    if (value) {
      this.setAttribute(attributes.MASK_TYPE, `${value}`);
      this.dataText?.setAttribute(attributes.MASK_TYPE, `${value}`);
    } else {
      this.removeAttribute(attributes.MASK_TYPE);
      this.dataText?.removeAttribute(attributes.MASK_TYPE);
    }
  }

  /**
   * Gets the current mask type
   * @returns {MaskType} The current mask type
   */
  get maskType(): MaskType { return this.getAttribute(attributes.MASK_TYPE) as MaskType; }

  /**
   * Sets the number of characters to reveal in masked text
   * @param {number | null} value The number of characters to reveal from the beginning of the text
   */
  set textMaskCharacter(value: number | null) {
    if (!value || !isValidInteger(value)) {
      this.removeAttribute(attributes.TEXT_MASK_CHARACTER);
      this.dataText?.removeAttribute(attributes.TEXT_MASK_CHARACTER);
      return;
    }

    const valueToSet = value > 4 ? 4 : value;
    this.setAttribute(attributes.TEXT_MASK_CHARACTER, `${valueToSet}`);
    this.dataText?.setAttribute(attributes.TEXT_MASK_CHARACTER, `${valueToSet}`);
  }

  get textMaskCharacter(): number {
    return this.getAttribute(attributes.TEXT_MASK_CHARACTER) ? Number(this.getAttribute(attributes.TEXT_MASK_CHARACTER)) : 0;
  }

  /**
   * Sets whether the text is masked or not
   * @param {boolean | string} value True to mask the text, false to show original text
   */
  set masked(value: boolean | string) {
    const valueSafe = stringToBool(value);
    if (valueSafe) {
      this.setAttribute(attributes.MASKED, '');
      this.dataText?.setAttribute(attributes.MASKED, '');
    } else {
      this.removeAttribute(attributes.MASKED);
      this.dataText?.removeAttribute(attributes.MASKED);
    }
  }

  /**
   * Gets whether the text is currently masked
   * @returns {boolean} True if the text is masked
   */
  get masked() {
    return this.dataText?.hasAttribute(attributes.MASKED) || false;
  }

  /**
   * Gets the description text element from the container.
   * @returns {IdsText | null} The IdsText element with class 'description' or null if not found.
   */
  get dataText(): IdsText | null {
    return this.container?.querySelector<IdsText>('ids-text.description') || null;
  }

  /**
   * Sets attribute value of font-weight to ids-data-label and to dataText
   * @param {string} value label string
   */
  set fontWeight(value: string) {
    if (value) {
      this.setAttribute(attributes.FONT_WEIGHT, value);
      this.dataText?.setAttribute(attributes.FONT_WEIGHT, value);
    } else {
      this.removeAttribute(attributes.FONT_WEIGHT);

      // reset to bold font weight which is default
      this.dataText?.setAttribute(attributes.FONT_WEIGHT, 'bold');
    }
  }

  /**
   * Get the font weight for the data text
   * @returns {string} The font weight value, defaults to 'bold' if not set
   */
  get fontWeight(): string {
    return this.getAttribute(attributes.FONT_WEIGHT) ?? 'bold';
  }

  /**
   * Sets to label
   * @param {string} value label string
   */
  set label(value: string) {
    if (value) {
      this.setAttribute(attributes.LABEL, value);
    } else {
      this.removeAttribute(attributes.LABEL);

      const isLabelExists = !!this.container?.querySelector('.label');
      if (isLabelExists) {
        this.container!.removeChild(this.container!.childNodes[0]);
      }
      return;
    }

    const label = this.container?.querySelector('.label');
    if (label) {
      label.innerHTML = `${value ?? ''}<span class="colon">:</span>`;
    } else {
      this.container!.innerHTML = `${this.labelText} ${this.container?.innerHTML}`;
    }
  }

  /**
   * Gets the label text from the attribute
   * @returns {string} The label text, empty string if not set
   */
  get label(): string {
    return this.getAttribute(attributes.LABEL) ?? '';
  }

  /**
   * Sets whether to show a colon after the label
   * @param {boolean} value If true, shows a colon after the label
   */
  set showColon(value: boolean) {
    const val = stringToBool(value);
    if (val) {
      this.setAttribute(attributes.SHOW_COLON, '');
    } else {
      this.removeAttribute(attributes.SHOW_COLON);
    }
  }

  /**
   * Gets whether a colon is shown after the label
   * @returns {boolean} True if a colon is shown after the label
   */
  get showColon(): boolean {
    return stringToBool(this.getAttribute(attributes.SHOW_COLON));
  }

  /**
   * Creates the HTML for the label text element with colon
   * @returns {string} The HTML string for the label element
   */
  get labelText(): string {
    return `<ids-text class="label" font-size="16">${this.label}<span class="colon">:</span></ids-text>`;
  }
}
